import { t } from '@hefe/i18n';
import {
  AppSlice,
  currentUserSelector,
  fetchNotificationTypesUpdates,
  ModalsSlice,
  newNotificationTypesSelector,
  PersistSystemSlice,
} from '@hefe/store';
import { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_DATA } from '../../../version';
import NewNotificationsAvailableComponent from '../../containers/notifications/components/new-notifications-available/new-notifications-available.component';
import NewVersionModalComponent from '../../containers/system/components/new-notifications-available/new-version-modal.component';
import { useAppDispatch } from '../../store/store';

/* eslint-disable-next-line */
export interface InfoPopupProviderProps {}

export default function InfoPopupProvider(
  props: PropsWithChildren<InfoPopupProviderProps>
) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useSelector(currentUserSelector);

  const notificationsUpdateLastCalledDate = useSelector(
    PersistSystemSlice.endpointLastCalledWithinMillisecondsSelector(
      fetchNotificationTypesUpdates.typePrefix,
      1000 * 60 * 60 * 12 // check every 12 hours
    )
  );

  const newNotificationTypes = useSelector(newNotificationTypesSelector);

  const currentVersion = useSelector(AppSlice.currentVersionSelector);

  useEffect(() => {
    if (!user) {
      return;
    }

    // check if notifications update was called in the last 24 hours
    if (!notificationsUpdateLastCalledDate) {
      dispatch(fetchNotificationTypesUpdates());
    }
  }, [notificationsUpdateLastCalledDate]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (newNotificationTypes.length > 0) {
      // Show Popup wth info that new notifications are available
      dispatch(
        ModalsSlice.modalsActions.openModal({
          title: t('notifications.new_categories.title'),
          component: (
            <NewNotificationsAvailableComponent
              onButtonClick={() => {
                dispatch(ModalsSlice.modalsActions.closeModal());
                navigate('/user/settings');
              }}
            />
          ),
          hideCancelButton: true,
          onOk: (closeModal) => {
            closeModal();
            navigate('/user/settings');
          },
        })
      );
    }
  }, [newNotificationTypes]);

  /******************************************************************************
   *
   *                             Version Check
   *
   *****************************************************************************/
  useEffect(() => {
    if (!currentVersion) {
      dispatch(AppSlice.actions.setCurrentVersion(APP_DATA.version));
    } else if (APP_DATA.version !== currentVersion) {
      dispatch(
        ModalsSlice.modalsActions.openModal({
          title: t('update.title'),
          component: (
            <NewVersionModalComponent
              version={APP_DATA.version}
              onButtonClick={() => {
                navigate('system/versions');
                dispatch(ModalsSlice.modalsActions.closeModal());
                dispatch(AppSlice.actions.setCurrentVersion(APP_DATA.version));
              }}
            />
          ),
          hideCancelButton: true,
          onOk: (closeModal) => {
            closeModal();
            dispatch(AppSlice.actions.setCurrentVersion(APP_DATA.version));
          },
        })
      );
    }
  }, [currentVersion, dispatch, navigate]);

  return <>{props.children}</>;
}
